import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Channel, DirectMessage, Message, TypingState } from '@/types';
import { useAuthStore } from './authStore';
import { seqMax } from '@/api/sync';
import { idbStorage } from '@/lib/idbStorage';

/**
 * Phase 15.1 — local-first cache.
 * Сохраняем в IndexedDB ограниченный набор полей: список каналов, курсоры
 * sync и последние N сообщений каждого чата. На reload UI рендерится
 * мгновенно из кэша, параллельно /api/sync/* догоняет дельту.
 *
 * НЕ сохраняем: typing/online/unread (ephemeral), pendingClientMessageIds
 * (внутри одной сессии), pendingMentionInject (UI-event).
 */
const MAX_PERSISTED_MESSAGES_PER_CHAT = 100;

interface ChatState {
  // Active channel/DM
  activeChannelId: string | null;
  activeDMUserId: string | null;

  // Messages cache: channelId -> Message[]
  channelMessages: Record<string, Message[]>;
  dmMessages: Record<string, DirectMessage[]>;

  // Typing
  typing: TypingState;
  dmTyping: Record<string, boolean>; // userId -> isTyping

  // Online users: userId -> status
  onlineUsers: Record<string, string>;

  // Joined channels list
  joinedChannels: Channel[];

  // Unread counts
  unreadCounts: Record<string, number>; // channelId -> count

  // ── Phase 7 — server-state sync ──────────────────────────────────────────
  // Последний известный серверный seq для каждого scope. Хранится как
  // string (BigInt). Используется при reconnect/wakeup для запроса дельты
  // через `/api/sync/*` или сокетного `sync:request`.
  channelSeq: Record<string, string>; // channelId -> seq
  dmSeq: Record<string, string>;      // partnerId -> seq
  // Идемпотентные ключи отправленных, но ещё не подтверждённых серверным
  // эхо сообщений. Когда приходит `message:new`/`dm:new` с тем же
  // clientMessageId, мы заменяем оптимистичную запись, не дублируя её.
  pendingClientMessageIds: Set<string>;

  // Actions
  setActiveChannel: (channelId: string | null) => void;
  setActiveDMUser: (userId: string | null) => void;

  addChannelMessage: (msg: Message) => void;
  updateChannelMessage: (msg: Message) => void;
  removeChannelMessage: (id: string, channelId: string) => void;
  setChannelMessages: (channelId: string, messages: Message[], prepend?: boolean) => void;

  addDMMessage: (msg: DirectMessage) => void;
  setDMMessages: (partnerId: string, messages: DirectMessage[], prepend?: boolean) => void;
  // Phase 16 — удалить одно DM по id. Находим partnerId внутри, так что
  // caller не должен знать с кем. Если dmId не найден — no-op.
  removeDmMessage: (dmId: string) => void;
  // Phase 16 — помечаем одно DM как deleted-for-all: меняем content
  // на «[message deleted]» и deletedAt, в UI отображается плашка. Нужно
  // для socket-события, когда собеседник удалил у всех (не мы).
  markDmDeleted: (dmId: string) => void;

  setTyping: (channelId: string, users: string[]) => void;
  setDMTyping: (userId: string, isTyping: boolean) => void;
  setUserStatus: (userId: string, status: string) => void;

  setJoinedChannels: (channels: Channel[]) => void;
  addJoinedChannel: (channel: Channel) => void;
  removeJoinedChannel: (channelId: string) => void;

  incrementUnread: (channelId: string) => void;
  clearUnread: (channelId: string) => void;

  // Phase 7 — sync state
  setChannelSeq: (channelId: string, seq: string) => void;
  setDmSeq: (partnerId: string, seq: string) => void;
  bumpChannelSeq: (channelId: string, seq: string | undefined) => void;
  bumpDmSeq: (partnerId: string, seq: string | undefined) => void;
  markPendingClientMessageId: (id: string) => void;
  clearPendingClientMessageId: (id: string) => void;
  hasPendingClientMessageId: (id: string) => boolean;

  // Phase 20.1 — IRC classic mention inject. `scopeKey` = channelId or
  // `dm:<userId>`. MessageInput reads + clears this on mount/change.
  pendingMentionInject: { scopeKey: string; text: string; nonce: number } | null;
  injectMention: (scopeKey: string, username: string) => void;
  clearMentionInject: () => void;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
  activeChannelId: null,
  activeDMUserId: null,
  channelMessages: {},
  dmMessages: {},
  typing: {},
  dmTyping: {},
  onlineUsers: {},
  joinedChannels: [],
  unreadCounts: {},
  channelSeq: {},
  dmSeq: {},
  pendingClientMessageIds: new Set<string>(),

  setActiveChannel: (channelId) =>
    set({ activeChannelId: channelId, activeDMUserId: null }),

  setActiveDMUser: (userId) =>
    set({ activeDMUserId: userId, activeChannelId: null }),

  addChannelMessage: (msg) =>
    set((s) => {
      const existing = s.channelMessages[msg.channelId] ?? [];
      // Phase 7 — dedupe по id (на случай sync ↔ realtime гонки) и по
      // clientMessageId (на случай оптимистичной отправки до прихода эха).
      let next = existing;
      const byId = existing.findIndex((m) => m.id === msg.id);
      const byCmid =
        byId === -1 && msg.clientMessageId
          ? existing.findIndex((m) => m.clientMessageId === msg.clientMessageId)
          : -1;
      if (byId !== -1) {
        next = existing.map((m, i) => (i === byId ? msg : m));
      } else if (byCmid !== -1) {
        next = existing.map((m, i) => (i === byCmid ? msg : m));
      } else {
        next = [...existing, msg];
      }
      // Bump per-channel cursor.
      const channelSeq = msg.seq
        ? { ...s.channelSeq, [msg.channelId]: seqMax(s.channelSeq[msg.channelId], msg.seq) }
        : s.channelSeq;
      // Если этот id был среди ожидающих оптимистичных — снимаем флаг.
      let pending = s.pendingClientMessageIds;
      if (msg.clientMessageId && pending.has(msg.clientMessageId)) {
        pending = new Set(pending);
        pending.delete(msg.clientMessageId);
      }
      return {
        channelMessages: { ...s.channelMessages, [msg.channelId]: next },
        channelSeq,
        pendingClientMessageIds: pending,
      };
    }),

  updateChannelMessage: (msg) =>
    set((s) => ({
      channelMessages: {
        ...s.channelMessages,
        [msg.channelId]: (s.channelMessages[msg.channelId] ?? []).map((m) =>
          m.id === msg.id ? msg : m,
        ),
      },
    })),

  removeChannelMessage: (id, channelId) =>
    set((s) => ({
      channelMessages: {
        ...s.channelMessages,
        [channelId]: (s.channelMessages[channelId] ?? []).map((m) =>
          m.id === id ? { ...m, deletedAt: new Date().toISOString(), content: '[message deleted]' } : m,
        ),
      },
    })),

  setChannelMessages: (channelId, messages, prepend = false) =>
    set((s) => ({
      channelMessages: {
        ...s.channelMessages,
        [channelId]: prepend
          ? [...messages, ...(s.channelMessages[channelId] ?? [])]
          : messages,
      },
    })),

  addDMMessage: (msg) => {
    const currentUserId = useAuthStore.getState().user?.id;
    const partnerId =
      msg.senderId === currentUserId ? msg.recipientId : msg.senderId;
    set((s) => {
      const existing = s.dmMessages[partnerId] ?? [];
      // Phase 7 — dedupe (см. addChannelMessage).
      let next = existing;
      const byId = existing.findIndex((m) => m.id === msg.id);
      const byCmid =
        byId === -1 && msg.clientMessageId
          ? existing.findIndex((m) => m.clientMessageId === msg.clientMessageId)
          : -1;
      if (byId !== -1) {
        next = existing.map((m, i) => (i === byId ? msg : m));
      } else if (byCmid !== -1) {
        next = existing.map((m, i) => (i === byCmid ? msg : m));
      } else {
        next = [...existing, msg];
      }
      const dmSeq = msg.seq
        ? { ...s.dmSeq, [partnerId]: seqMax(s.dmSeq[partnerId], msg.seq) }
        : s.dmSeq;
      let pending = s.pendingClientMessageIds;
      if (msg.clientMessageId && pending.has(msg.clientMessageId)) {
        pending = new Set(pending);
        pending.delete(msg.clientMessageId);
      }
      return {
        dmMessages: { ...s.dmMessages, [partnerId]: next },
        dmSeq,
        pendingClientMessageIds: pending,
      };
    });
  },

  setDMMessages: (partnerId, messages, prepend = false) =>
    set((s) => ({
      dmMessages: {
        ...s.dmMessages,
        [partnerId]: prepend
          ? [...messages, ...(s.dmMessages[partnerId] ?? [])]
          : messages,
      },
    })),

  // Phase 16 — убираем DM из всех «окон» одним махом. Partner неизвестен
  // → идём по всем парам.
  removeDmMessage: (dmId) =>
    set((s) => {
      const updated: Record<string, DirectMessage[]> = {};
      let changed = false;
      for (const [partnerId, list] of Object.entries(s.dmMessages)) {
        const filtered = list.filter((m) => m.id !== dmId);
        if (filtered.length !== list.length) changed = true;
        updated[partnerId] = filtered;
      }
      return changed ? { dmMessages: updated } : s;
    }),

  // Phase 16 — пометить DM как «удалено у всех». Меняем content и
  // deletedAt — рендер message bubble сам покажет плашку «Сообщение удалено».
  markDmDeleted: (dmId) =>
    set((s) => {
      const updated: Record<string, DirectMessage[]> = {};
      let changed = false;
      for (const [partnerId, list] of Object.entries(s.dmMessages)) {
        const next = list.map((m) =>
          m.id === dmId
            ? { ...m, content: '[message deleted]', deletedAt: new Date().toISOString() }
            : m,
        );
        if (next.some((m, i) => m !== list[i])) changed = true;
        updated[partnerId] = next;
      }
      return changed ? { dmMessages: updated } : s;
    }),

  setTyping: (channelId, users) =>
    set((s) => ({ typing: { ...s.typing, [channelId]: users } })),

  setDMTyping: (userId, isTyping) =>
    set((s) => ({ dmTyping: { ...s.dmTyping, [userId]: isTyping } })),

  setUserStatus: (userId, status) =>
    set((s) => ({ onlineUsers: { ...s.onlineUsers, [userId]: status } })),

  setJoinedChannels: (channels) => set({ joinedChannels: channels }),

  addJoinedChannel: (channel) =>
    set((s) => ({
      joinedChannels: s.joinedChannels.some((c) => c.id === channel.id)
        ? s.joinedChannels
        : [...s.joinedChannels, channel],
    })),

  removeJoinedChannel: (channelId) =>
    set((s) => ({ joinedChannels: s.joinedChannels.filter((c) => c.id !== channelId) })),

  incrementUnread: (channelId) =>
    set((s) => ({
      unreadCounts: { ...s.unreadCounts, [channelId]: (s.unreadCounts[channelId] ?? 0) + 1 },
    })),

  clearUnread: (channelId) =>
    set((s) => ({ unreadCounts: { ...s.unreadCounts, [channelId]: 0 } })),

  // ── Phase 7 — sync state actions ───────────────────────────────────────
  setChannelSeq: (channelId, seq) =>
    set((s) => ({ channelSeq: { ...s.channelSeq, [channelId]: seq } })),
  setDmSeq: (partnerId, seq) =>
    set((s) => ({ dmSeq: { ...s.dmSeq, [partnerId]: seq } })),
  bumpChannelSeq: (channelId, seq) =>
    set((s) =>
      seq
        ? { channelSeq: { ...s.channelSeq, [channelId]: seqMax(s.channelSeq[channelId], seq) } }
        : s,
    ),
  bumpDmSeq: (partnerId, seq) =>
    set((s) =>
      seq ? { dmSeq: { ...s.dmSeq, [partnerId]: seqMax(s.dmSeq[partnerId], seq) } } : s,
    ),
  markPendingClientMessageId: (id) =>
    set((s) => {
      const next = new Set(s.pendingClientMessageIds);
      next.add(id);
      return { pendingClientMessageIds: next };
    }),
  clearPendingClientMessageId: (id) =>
    set((s) => {
      if (!s.pendingClientMessageIds.has(id)) return s;
      const next = new Set(s.pendingClientMessageIds);
      next.delete(id);
      return { pendingClientMessageIds: next };
    }),
  hasPendingClientMessageId: (id: string): boolean => get().pendingClientMessageIds.has(id),

  pendingMentionInject: null,
  injectMention: (scopeKey, username) =>
    set({ pendingMentionInject: { scopeKey, text: `@${username} `, nonce: Date.now() } }),
  clearMentionInject: () => set({ pendingMentionInject: null }),
    }),
    {
      name: 'chat-store',
      version: 1,
      storage: createJSONStorage(() => idbStorage),
      // Partialize: сохраняем только «тяжёлые полезные» поля. UI-state и
      // ephemeral поля (typing, online, pending) не трогаем.
      partialize: (state) => ({
        joinedChannels: state.joinedChannels,
        channelSeq: state.channelSeq,
        dmSeq: state.dmSeq,
        // LRU-обрезание: храним последние N сообщений каждого чата.
        // Иначе IDB раздуется до сотен мегабайт у активного пользователя.
        channelMessages: Object.fromEntries(
          Object.entries(state.channelMessages).map(([k, v]) => [
            k, v.slice(-MAX_PERSISTED_MESSAGES_PER_CHAT),
          ]),
        ),
        dmMessages: Object.fromEntries(
          Object.entries(state.dmMessages).map(([k, v]) => [
            k, v.slice(-MAX_PERSISTED_MESSAGES_PER_CHAT),
          ]),
        ),
        unreadCounts: state.unreadCounts,
      }),
    },
  ),
);
